// src/components/quiz/questionTypes/AudioListeningQuestion.tsx
// FIXED VERSION - Audio is now OPTIONAL for type 3 questions
// Supports: audio + multi-select checkbox AND checkbox-only (no audio)

import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Alert,
  FormControl,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HeadsetIcon from "@mui/icons-material/Headset";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ChecklistIcon from "@mui/icons-material/Checklist";

// Types
interface AudioListeningQuestionData {
  id: string;
  type: "3";
  question: string;
  points: number;
  module: string;
  skill: string;
  week: number;
  program: string;
  audioUrl?: string; // NOW OPTIONAL
  options: string[];
  correctAnswers?: number[];
  tip?: string;
  numberOfCorrect?: number;
}

interface AudioListeningQuestionProps {
  question: AudioListeningQuestionData;
  onAnswerChange: (selectedAnswers: number[]) => void;
  userAnswer?: number[];
  disabled?: boolean;
  showResults?: boolean;
  reviewMode?: boolean;
}

// Styled components
const QuestionContainer = styled(Box)({
  maxWidth: "1000px",
  margin: "0 auto",
});

const QuestionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const AudioContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  textAlign: "center",
}));

const AudioControlsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: "white",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[1],
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  width: 72,
  height: 72,
  backgroundColor: theme.palette.primary.main,
  color: "white",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "scale(1.05)",
  },
  "&:disabled": {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
  transition: "all 0.3s ease",
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.grey[100],
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
    transform: "scale(1.1)",
  },
  "&:disabled": {
    backgroundColor: theme.palette.grey[100],
  },
  transition: "all 0.2s ease",
}));

const AudioProgress = styled(Box)({
  width: "100%",
  maxWidth: 400,
  margin: "0 auto",
});

const OptionsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

interface OptionItemProps {
  isCorrect?: boolean;
  isIncorrect?: boolean;
  showFeedback?: boolean;
}

const OptionItem = styled(FormControlLabel, {
  shouldForwardProp: (prop) =>
    !["isCorrect", "isIncorrect", "showFeedback"].includes(prop as string),
})<OptionItemProps>(({ theme, isCorrect, isIncorrect, showFeedback }) => ({
  width: "100%",
  margin: theme.spacing(1, 0),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `2px solid ${
    showFeedback && isCorrect
      ? theme.palette.success.main
      : showFeedback && isIncorrect
      ? theme.palette.error.main
      : theme.palette.divider
  }`,
  backgroundColor:
    showFeedback && isCorrect
      ? theme.palette.success.light + "20"
      : showFeedback && isIncorrect
      ? theme.palette.error.light + "20"
      : theme.palette.background.paper,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: showFeedback ? undefined : theme.palette.action.hover,
    borderColor: showFeedback ? undefined : theme.palette.primary.light,
  },
}));

const AudioListeningQuestion: React.FC<AudioListeningQuestionProps> = ({
  question,
  onAnswerChange,
  userAnswer,
  disabled = false,
  showResults = false,
  reviewMode = false,
}) => {
  const theme = useTheme<Theme>();
  const audioRef = useRef<HTMLAudioElement>(null);

  // Determine if this question has audio
  const hasAudio = Boolean(
    question.audioUrl && question.audioUrl.trim().length > 0
  );

  // ============================================================
  // Audio player state (only used when audio exists)
  // userAnswer prop is the single source of truth for selections
  // ============================================================
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [playCount, setPlayCount] = useState(0);

  // Derive selected answers from prop (controlled component)
  const selectedAnswers: number[] = Array.isArray(userAnswer) ? userAnswer : [];

  // Audio event handlers - only attach when audio exists
  useEffect(() => {
    if (!hasAudio) return;

    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedData = () => {
      setAudioLoaded(true);
      setDuration(audio.duration);
      setAudioError(null);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = () => {
      setAudioError(
        "Failed to load audio. Please check your internet connection."
      );
      setAudioLoaded(false);
    };

    // Use loadedmetadata (not loadeddata): with preload="metadata" the browser
    // fetches only metadata and may never fire loadeddata until playback starts.
    // loadedmetadata fires reliably and gives us the duration we need.
    audio.addEventListener("loadedmetadata", handleLoadedData);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedData);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [hasAudio]);

  // ============================================================
  // Audio Controls
  // ============================================================
  const handlePlayPause = useCallback(() => {
    if (!hasAudio) return;
    const audio = audioRef.current;
    if (!audio || !audioLoaded) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
      if (currentTime === 0) {
        setPlayCount((prev) => prev + 1);
      }
    }
  }, [hasAudio, audioLoaded, isPlaying, currentTime]);

  const handleReplay = useCallback(() => {
    if (!hasAudio) return;
    const audio = audioRef.current;
    if (!audio || !audioLoaded) return;

    audio.currentTime = 0;
    setCurrentTime(0);
    audio.play();
    setIsPlaying(true);
    setPlayCount((prev) => prev + 1);
  }, [hasAudio, audioLoaded]);

  const handleVolumeToggle = useCallback(() => {
    if (!hasAudio) return;
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = 1;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [hasAudio, isMuted]);

  // ============================================================
  // Answer Selection - Controlled Component Pattern
  // ============================================================
  const handleOptionToggle = useCallback(
    (optionIndex: number) => {
      if (disabled || reviewMode) return;

      const newAnswers = selectedAnswers.includes(optionIndex)
        ? selectedAnswers.filter((index) => index !== optionIndex)
        : [...selectedAnswers, optionIndex];

      onAnswerChange(newAnswers);
    },
    [disabled, reviewMode, selectedAnswers, onAnswerChange]
  );

  // ============================================================
  // Helper functions
  // ============================================================
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const showFeedback = showResults || reviewMode;

  const isOptionCorrect = (index: number): boolean => {
    return question?.correctAnswers?.includes(index) ?? false;
  };

  const isOptionIncorrect = (index: number): boolean => {
    return selectedAnswers.includes(index) && !isOptionCorrect(index);
  };

  // ============================================================
  // Validation - AFTER all hooks
  // Only check for options (audio is now optional)
  // ============================================================
  if (!question || !question.options || question.options.length === 0) {
    return (
      <Alert severity="error">
        Invalid question data: No options provided for this question.
      </Alert>
    );
  }

  // ============================================================
  // Determine instruction text based on audio presence
  // ============================================================
  const expectedCorrectCount =
    question.numberOfCorrect ?? question.correctAnswers?.length;
  const instructionText = hasAudio
    ? `Listen to the audio and select all correct answers. You may replay the audio as needed.`
    : expectedCorrectCount
    ? `Read the sentences carefully and select the ${expectedCorrectCount} correct option${
        expectedCorrectCount > 1 ? "s" : ""
      }.`
    : `Read the sentences carefully and select all correct answers.`;

  // ============================================================
  // Render
  // ============================================================
  return (
    <QuestionContainer>
      {/* Hidden Audio Element - only render when audio exists */}
      {hasAudio && (
        <audio
          controls
          controlsList="nodownload"
          ref={audioRef}
          src={question.audioUrl || ""}
          preload="metadata"
          style={{ display: "none" }}
        />
      )}

      {/* Question Header */}
      <QuestionHeader elevation={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              {question.question}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {question.skill} • {question.points} points
            </Typography>
          </Box>
          <Chip
            label={`${question.module} - Week ${question.week}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </QuestionHeader>

      {/* Tip (if provided, e.g. grammar hints) */}
      {question.tip && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> {question.tip}
          </Typography>
        </Alert>
      )}

      {/* Audio Player - ONLY rendered when audioUrl exists */}
      {hasAudio && (
        <AudioContainer elevation={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              justifyContent: "center",
            }}
          >
            <HeadsetIcon sx={{ fontSize: 36, color: "primary.main" }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
              >
                Audio Recording
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {audioLoaded
                  ? `Duration: ${formatTime(duration)}`
                  : "Loading audio..."}
                {playCount > 0 &&
                  ` • Played: ${playCount} time${playCount > 1 ? "s" : ""}`}
              </Typography>
            </Box>
          </Box>

          {audioError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {audioError}
            </Alert>
          ) : (
            <AudioControlsContainer>
              <Tooltip title={isPlaying ? "Pause" : "Play"}>
                <span>
                  <PlayButton onClick={handlePlayPause} disabled={!audioLoaded}>
                    {isPlaying ? (
                      <PauseIcon sx={{ fontSize: 36 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 36 }} />
                    )}
                  </PlayButton>
                </span>
              </Tooltip>

              <Tooltip title="Replay from beginning">
                <span>
                  <ControlButton onClick={handleReplay} disabled={!audioLoaded}>
                    <ReplayIcon />
                  </ControlButton>
                </span>
              </Tooltip>

              <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                <span>
                  <ControlButton
                    onClick={handleVolumeToggle}
                    disabled={!audioLoaded}
                  >
                    {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </ControlButton>
                </span>
              </Tooltip>
            </AudioControlsContainer>
          )}

          {/* Audio Progress Bar */}
          {audioLoaded && (
            <AudioProgress>
              <LinearProgress
                variant="determinate"
                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 1,
                  backgroundColor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 4,
                  },
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(currentTime)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(duration)}
                </Typography>
              </Box>
            </AudioProgress>
          )}
        </AudioContainer>
      )}

      {/* Instructions */}
      <Alert
        severity="info"
        icon={hasAudio ? <HeadsetIcon /> : <ChecklistIcon />}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        <Typography variant="body2">{instructionText}</Typography>
      </Alert>

      {/* Answer Options */}
      <OptionsContainer>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 2 }}
        >
          Select all that apply
          {expectedCorrectCount ? ` (${expectedCorrectCount} correct)` : ""}:
        </Typography>

        <FormControl component="fieldset" sx={{ width: "100%" }}>
          {question.options.map((option, index) => (
            <OptionItem
              key={index}
              isCorrect={showFeedback && isOptionCorrect(index)}
              isIncorrect={showFeedback && isOptionIncorrect(index)}
              showFeedback={showFeedback}
              control={
                <Checkbox
                  checked={selectedAnswers.includes(index)}
                  onChange={() => handleOptionToggle(index)}
                  disabled={disabled || reviewMode}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1">{option}</Typography>
                  {showFeedback && isOptionCorrect(index) && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {showFeedback && isOptionIncorrect(index) && (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Box>
              }
            />
          ))}
        </FormControl>
      </OptionsContainer>

      {/* Selection count indicator */}
      {selectedAnswers.length > 0 && !showFeedback && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Chip
            label={`${selectedAnswers.length} option${
              selectedAnswers.length !== 1 ? "s" : ""
            } selected${
              expectedCorrectCount ? ` of ${expectedCorrectCount} expected` : ""
            }`}
            color={
              expectedCorrectCount &&
              selectedAnswers.length === expectedCorrectCount
                ? "success"
                : "primary"
            }
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {/* Results feedback */}
      {showFeedback && question.correctAnswers && (
        <Alert
          severity={
            selectedAnswers.length === question.correctAnswers.length &&
            selectedAnswers.every((a) => question.correctAnswers!.includes(a))
              ? "success"
              : "info"
          }
          sx={{ mt: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            <strong>Correct answers:</strong>{" "}
            {question.correctAnswers.map((i) => question.options[i]).join(", ")}
          </Typography>
        </Alert>
      )}
    </QuestionContainer>
  );
};

export default AudioListeningQuestion;
